// 백엔드 API 서비스
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000/api');

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // HTTP 요청 헬퍼
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async saveSentence(text, targetLang = 'KO', sourceLang = null) {
    return this.request('/translation/sentence-save', {
      method: 'POST',
      body: JSON.stringify({
        text,
        targetLang,
        sourceLang
      })
    });
  }



  // 단일 텍스트 번역
  async translateText(text, targetLang = 'KO', sourceLang = null) {
    return this.request('/translation/single', {
      method: 'POST',
      body: JSON.stringify({
        text,
        targetLang,
        sourceLang
      })
    });
  }

  // 배치 번역 (여러 단어 동시 번역)
  async translateBatch(texts, targetLang = 'KO', sourceLang = null) {
    return this.request('/translation/batch', {
      method: 'POST',
      body: JSON.stringify({
        texts,
        targetLang,
        sourceLang
      })
    });
  }

  // 지원 언어 목록 조회
  async getSupportedLanguages() {
    return this.request('/translation/languages');
  }

  // 단어 히스토리 저장
  async saveWordHistory(userId, wordData) {
    // wordData: { word, translation, exampleSentence }
    return this.request('/words/history', {
      method: 'POST',
      body: JSON.stringify({ userId, ...wordData })
    });
  }

  // 단어 히스토리 조회
  async getWordHistory(userId, page = 1, limit = 20, sortBy = 'date') {
    return this.request(`/words/history?userId=${userId}&page=${page}&limit=${limit}&sortBy=${sortBy}`);
  }

  // 문장 히스토리 조회
  async getSentenceHistory(userId, page = 1, limit = 20) {
    return this.request(`/translation/history?userId=${userId}&page=${page}&limit=${limit}`);
  }

  // 즐겨찾기 단어 저장
  async saveFavoriteWord(wordId, isFavorite) {
    return this.request('/words/favorite', {
      method: 'POST',
      body: JSON.stringify({ wordId, isFavorite })
    });
  }

  // 단어 삭제
  async deleteWord(wordId) {
    return this.request(`/words/${wordId}`, {
      method: 'DELETE'
    });
  }

  // 즐겨찾기 단어 목록 조회
  async getFavoriteWords(userId) {
    return this.request(`/words/favorites?userId=${userId}`);
  }

  // 문장 유사도 검사
  async sentenceMatch(sentences) {
    return this.request('/translation/sentence-match', {
      method: 'POST',
      body: JSON.stringify({ sentences }),
    });
  }

  async checkSentenceHistory(userId, text, feedId) {
    return this.request('/translation/check-history', {
      method: 'POST',
      body: JSON.stringify({ userId, text, feedId })
    });
  }

  async checkSentenceHistoryBatch(userId, feedIds) {
    return this.request('/translation/history/batch-check', {
      method: 'POST',
      body: JSON.stringify({ userId, feedIds })
    });
  }

  // 사용자 문장 및 결과 저장 (백엔드 /translation/sentence-save 호출)
  // params: userId, sentences(resultJson), originalSentences, options
  async saveUserSentence(userId, sentences, originalSentences = null, options = {}) {
    return this.request('/translation/sentence-save', {
      method: 'POST',
      body: JSON.stringify({ userId, sentences, originalSentences, options }),
    });
  }

  async updateUserSentence(docId, sentences) {
    return this.request('/translation/sentence-update', {
      method: 'PUT',
      body: JSON.stringify({ docId, sentences }),
    });
  }

  // 단어 의미 요청 (ChatGPT)
  async wordMeaning({ source, target, word, userId }) {
    console.log(`[apiService] wordMeaning called for word: "${word}"`);
    console.trace("[apiService] wordMeaning call stack:");
    return this.request('/translation/word-meaning', {
      method: 'POST',
      body: JSON.stringify({ source, target, word, userId }),
    });
  }

  // 문장 전체 분석 (Pre-fetch)
  async analyzeSentence({ source, target }) {
    return this.request('/translation/analyze', {
      method: 'POST',
      body: JSON.stringify({ source, target })
    });
  }

  // 문장 유사도 검사 (스트리밍)
  async streamSentenceMatch(sentences, onData) {
    const url = `${this.baseURL}/translation/sentence-match-stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sentences }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop(); // Keep incomplete chunk

      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            if (data.error) throw new Error(data.error);
            onData(data);
          } catch (e) {
            console.error('SSE Parse Error', e);
          }
        }
      }
    }
  }

  // 구글 로그인
  async loginWithGoogle(token) {
    return this.request('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  // 퀴즈 단어 가져오기
  async getQuizWords(userId, mode) {
    const url = mode
      ? `/words/quiz?userId=${userId}&mode=${mode}`
      : `/words/quiz?userId=${userId}`;
    return this.request(url);
  }

  // 단어 상태 업데이트 (암기/복습필요 등)
  async updateWordStatus(wordId, status) {
    // status: { isMemorized: boolean, reviewNeeded: boolean }
    return this.request(`/words/${wordId}/status`, {
      method: "PATCH",
      body: JSON.stringify(status)
    });
  }

  // 대시보드 통계 조회
  async getDashboardStats(userId) {
    return this.request(`/dashboard?userId=${userId}`);
  }

  // 게스트 초기화 (하루 1회)
  async cleanupGuestData() {
    return this.request('/auth/guest-cleanup', {
      method: 'POST'
    });
  }
  // AI 학습 자료 생성
  async generateStudyContent({ target, level, topic }) {
    return this.request('/study/generate', {
      method: 'POST',
      body: JSON.stringify({
        target, level, topic
      })
    });
  }

  // 추천 주제 가져오기
  async getStudyTopics(target) {
    return this.request(`/study/topics?target=${target}`);
  }

  // 피드 가져오기
  // 피드 가져오기
  // Supports legacy: getFeed(type, contentType) OR new: getFeed({ type, contentType, topics })
  async getFeed(arg1 = 'init', arg2 = null) {
    let type = 'init';
    let contentType = null;
    let topics = null;
    let lastCreatedAt = null;

    if (typeof arg1 === 'object') {
      type = arg1.type || 'init';
      contentType = arg1.contentType;
      topics = arg1.topics;
      lastCreatedAt = arg1.lastCreatedAt;
    } else {
      type = arg1;
      contentType = arg2;
    }

    const params = new URLSearchParams();
    params.append('type', type);
    if (contentType) params.append('contentType', contentType);
    if (topics && Array.isArray(topics) && topics.length > 0) {
      params.append('topics', topics.join(','));
    }
    if (arg1.limit) params.append('limit', arg1.limit); // Added limit support
    if (lastCreatedAt) params.append('lastCreatedAt', lastCreatedAt);

    return this.request(`/feed?${params.toString()}`);
  }
  // 피드 아이템 좋아요
  async likeFeedItem(id) {
    return this.request(`/feed/${id}/like`, { method: 'POST' });
  }

  // 피드 단건 강제 생성
  async generateFeedItem() {
    return this.request('/feed/generate', { method: 'POST' });
  }

  // AI 스토리 생성
  async generateStory(options) {
    return this.request('/story/generate', {
      method: 'POST',
      body: JSON.stringify(options)
    });
  }

  // 스토리 주제 추천
  async getStoryTopics(genre) {
    return this.request(`/story/topics?genre=${encodeURIComponent(genre)}`);
  }

  // 사용자 스토리 목록 조회
  async getUserStories(userId, limit = 20) {
    return this.request(`/story?userId=${userId}&limit=${limit}`);
  }

  // 스토리 저장
  async saveStory(userId, storyData) {
    return this.request('/story/save', {
      method: 'POST',
      body: JSON.stringify({ userId, storyData })
    });
  }

  // 스토리 분할 상태 업데이트
  async updateStorySplit(storyId, splitData) {
    return this.request(`/story/${storyId}/split`, {
      method: 'PATCH',
      body: JSON.stringify(splitData)
    });
  }

  // Simple string hash for cache keys
  _simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  // --- Batch ---
  async runNewsBatch(testMode = false) {
    try {
      const response = await this.request('/batch/news', {
        method: 'POST',
        body: JSON.stringify({ testMode })
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteNews() {
    return this.request('/batch/news', { method: 'DELETE' });
  }

  async getBatchStatus() {
    return this.request('/batch/status');
  }

  // Server-Side TTS (Premium) with Cache
  async generateAudio(text, voice = 'alloy', speed = 1.0) {
    // 1. Generate Stable Cache Key (Simulated GET URL)
    // We use a hash of the text to keep the URL short and safe
    const textHash = this._simpleHash(text + voice + speed);
    const cacheKey = `${this.baseURL}/tts-cache/${textHash}`;

    try {
      // 2. Try Cache Look-up
      if ('caches' in window) {
        const cache = await caches.open('nureum-tts-v1');
        const cachedResponse = await cache.match(cacheKey);

        if (cachedResponse) {
          // console.log("TTS Cache Hit!");
          return await cachedResponse.blob();
        }
      }
    } catch (e) {
      console.warn("TTS Cache check failed", e);
    }

    // 3. Fetch from Server
    const response = await fetch(`${this.baseURL}/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ text, voice, speed })
    });

    if (!response.ok) {
      throw new Error('TTS Generation failed');
    }

    // Check Content-Type to see if it's JSON (URL) or Blob (Audio)
    const contentType = response.headers.get('content-type');
    let blob;

    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      if (data.audioUrl) {
        // Verify URL is accessible (optional, or just return fetch of it)
        // For consistency with current interface returning Blob, we fetch the URL as blob
        // BUT since we want to cache, maybe we should cache the URL now?
        // The current contract returns a BLOB. Let's fetch the blob from the URL.
        const audioRes = await fetch(data.audioUrl);
        blob = await audioRes.blob();
      }
    } else {
      // Fallback for direct binary response
      blob = await response.blob();
    }

    if (!blob) throw new Error("Invalid TTS response");

    // 4. Save to Cache
    try {
      if ('caches' in window) {
        const cache = await caches.open('nureum-tts-v1');
        const responseToCache = new Response(blob.slice(0, blob.size, "audio/mpeg"), {
          status: 200,
          headers: { 'Content-Type': 'audio/mpeg' }
        });
        await cache.put(cacheKey, responseToCache);
      }
    } catch (e) {
      console.warn("TTS Cache put failed", e);
    }

    return blob;
  }

  // TTS Health Check
  async checkTTSHealth() {
    return this.request('/tts/health');
  }
  // Stock Report
  async getStockReport(date = 'latest') {
    return this.request(`/batch/stock/report/${date}`);
  }

  // Trigger Stock Batch
  async runStockBatch() {
    return this.request('/batch/stock', { method: 'POST' });
  }

  // Trigger Stock Evaluation Batch
  async runEvaluationBatch() {
    return this.request('/batch/stock/evaluate', { method: 'POST' });
  }

  // --- Stock Technical Analysis ---
  async searchStocks(keyword) {
    return this.request(`/stock/search?keyword=${encodeURIComponent(keyword)}`);
  }

  async getStockPrice(symbol) {
    return this.request(`/stock/price/${symbol}`);
  }

  // --- Livermore Analysis ---
  async getLivermoreLeaders(force = false) {
    return this.request(`/livermore/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreHistory() {
    return this.request('/livermore/history');
  }

  async getLivermoreHistoryDetail(id) {
    return this.request(`/livermore/history/${id}`);
  }

  async saveLivermoreSnapshot() {
    return this.request('/livermore/snapshot', { method: 'POST' });
  }

  async calculateLivermorePerformance(id, force = false) {
    return this.request(`/livermore/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async syncMinuteHistory(onData) {
    const url = `${this.baseURL}/livermore/sync-minute-history`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}), // 명시적으로 빈 바디 전달
    });

    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        const trimmedPart = part.trim();
        if (trimmedPart.startsWith('data: ')) {
          try {
            const data = JSON.parse(trimmedPart.slice(6));
            onData(data);
          } catch (e) {
            console.error('Sync Stream Parse Error (Invalid JSON)', e, trimmedPart);
          }
        }
      }
    }
  }

  // --- Livermore V2.0 ---
  async getLivermoreV20Leaders(force = false) {
    return this.request(`/livermore-v20/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV20History() {
    return this.request('/livermore-v20/history');
  }

  async getLivermoreV20HistoryDetail(id) {
    return this.request(`/livermore-v20/history/${id}`);
  }

  async saveLivermoreV20Snapshot() {
    return this.request('/livermore-v20/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV20Performance(id, force = false) {
    return this.request(`/livermore-v20/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  // --- Livermore V2.1 ---
  async getLivermoreV21Leaders(force = false) {
    return this.request(`/livermore-v21/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV21History() {
    return this.request('/livermore-v21/history');
  }

  async getLivermoreV21HistoryDetail(id) {
    return this.request(`/livermore-v21/history/${id}`);
  }

  async saveLivermoreV21Snapshot() {
    return this.request('/livermore-v21/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV21Performance(id, force = false) {
    return this.request(`/livermore-v21/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async getLivermoreV21Scheduler() {
    return this.request('/livermore-v21/scheduler');
  }

  async updateLivermoreV21Scheduler(config) {
    return this.request('/livermore-v21/scheduler', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // --- Livermore V3.0 ---
  async getLivermoreV30Leaders(force = false) {
    return this.request(`/livermore-v30/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV30History() {
    return this.request('/livermore-v30/history');
  }

  async getLivermoreV30HistoryDetail(id) {
    return this.request(`/livermore-v30/history/${id}`);
  }

  async saveLivermoreV30Snapshot() {
    return this.request('/livermore-v30/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV30Performance(id, force = false) {
    return this.request(`/livermore-v30/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async getLivermoreV30Scheduler() {
    return this.request('/livermore-v30/scheduler');
  }

  async updateLivermoreV30Scheduler(config) {
    return this.request('/livermore-v30/scheduler', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // --- Livermore V3.1 ---
  async getLivermoreV31Leaders(force = false) {
    return this.request(`/livermore-v31/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV31History() {
    return this.request('/livermore-v31/history');
  }

  async getLivermoreV31HistoryDetail(id) {
    return this.request(`/livermore-v31/history/${id}`);
  }

  async saveLivermoreV31Snapshot() {
    return this.request('/livermore-v31/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV31Performance(id, force = false) {
    return this.request(`/livermore-v31/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async getLivermoreV31Scheduler() {
    return this.request('/livermore-v31/scheduler');
  }

  async updateLivermoreV31Scheduler(config) {
    return this.request('/livermore-v31/scheduler', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // --- Livermore V4.0 (점수·랭킹) ---
  async getLivermoreV40Leaders(force = false) {
    return this.request(`/livermore-v40/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV40History(cursor = null) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.request(`/livermore-v40/history${qs}`);
  }

  async getLivermoreV40HistoryDetail(id) {
    return this.request(`/livermore-v40/history/${id}`);
  }

  async saveLivermoreV40Snapshot() {
    return this.request('/livermore-v40/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV40Performance(id, force = false) {
    return this.request(`/livermore-v40/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async getLivermoreV40Scheduler() {
    return this.request('/livermore-v40/scheduler');
  }

  async updateLivermoreV40Scheduler(config) {
    return this.request('/livermore-v40/scheduler', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  async getLivermoreV40RawCandidates() {
    return this.request('/livermore-v40/raw-candidates');
  }

  async checkLivermoreV40ApiHealth() {
    return this.request('/livermore-v40/api-check');
  }

  // --- Livermore V4.2 (Dual Track: Flat Base + Momentum) ---
  async getLivermoreV42Leaders(force = false) {
    return this.request(`/livermore-v42/leaders${force ? '?force=true' : ''}`);
  }

  async getLivermoreV42History(cursor = null) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return this.request(`/livermore-v42/history${qs}`);
  }

  async getLivermoreV42HistoryDetail(id) {
    return this.request(`/livermore-v42/history/${id}`);
  }

  async saveLivermoreV42Snapshot() {
    return this.request('/livermore-v42/snapshot', { method: 'POST' });
  }

  async calculateLivermoreV42Performance(id, force = false) {
    return this.request(`/livermore-v42/history/${id}/performance?force=${force}`, { method: 'POST' });
  }

  async getLivermoreV42Scheduler() {
    return this.request('/livermore-v42/scheduler');
  }

  async updateLivermoreV42Scheduler(config) {
    return this.request('/livermore-v42/scheduler', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // --- Scheduler ---
  async getLivermoreScheduler() {
    return this.request('/livermore/scheduler');
  }

  async updateLivermoreScheduler(config) {
    return this.request('/livermore/scheduler', { 
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  async getLivermoreV20Scheduler() {
    return this.request('/livermore-v20/scheduler');
  }

  async updateLivermoreV20Scheduler(config) {
    return this.request('/livermore-v20/scheduler', { 
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  // --- Wyckoff ---
  async getWyckoffSprings() {
    return this.request('/wyckoff/detect');
  }

  async getStockInfo(symbol) {
    return this.request(`/stock/info/${symbol}`);
  }

  async getStockCandles(symbol, period = 'D', type = 'stock', to = null) {
    const encodedSymbol = encodeURIComponent(symbol);
    const url = `/stock/candles/${encodedSymbol}?period=${period}&type=${type}${to ? `&to=${to}` : ''}`;
    return this.request(url);
  }

  async getTechnicalIndicators(symbol, period = 'D', type = 'stock') {
    return this.request(`/stock/indicators/${symbol}?period=${period}&type=${type}`);
  }

  // --- Manual Sync ---
  async collectStockData(symbol, periods = null) {
    return this.request('/batch/collect', {
      method: 'POST',
      body: JSON.stringify({ symbol, periods })
    });
  }

  async syncMarketCap() {
    return this.request('/stock/sync/market-cap', { method: 'POST' });
  }

  async runBacktest(symbol, strategy = 'RSI', period = 'D') {
    return this.request('/stock/backtest', {
      method: 'POST',
      body: JSON.stringify({ symbol, strategy, period })
    });
  }

  // --- Turtle Trading ---
  async getTurtleConfig() {
    return this.request('/turtle/config');
  }

  async updateTurtleConfig(config) {
    return this.request('/turtle/config', {
      method: 'POST',
      body: JSON.stringify(config)
    });
  }

  async getTurtleOpenPositions() {
    return this.request('/turtle/positions');
  }

  async getTurtleAllPositions() {
    return this.request('/turtle/positions/all');
  }

  async getTurtleTrades(limit = 200, symbol = null) {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit);
    if (symbol) params.append('symbol', symbol);
    return this.request(`/turtle/trades?${params.toString()}`);
  }

  async getTurtlePerformance(limit = 60) {
    return this.request(`/turtle/performance?limit=${limit}`);
  }

  async getTurtleSignalPreview() {
    return this.request('/turtle/signal-preview');
  }

  async runTurtleScan() {
    return this.request('/turtle/run', { method: 'POST' });
  }

  async runTurtleScanStream(onData) {
    const url = `${this.baseURL}/turtle/run-stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            onData(data);
          } catch (e) {
            console.error('SSE Parse Error', e);
          }
        }
      }
    }
  }

  async resetTurtleAll() {
    return this.request('/turtle/reset', { method: 'POST' });
  }

  async getTurtleStats() {
    return this.request('/turtle/stats');
  }

  async getTurtleScreener() {
    return this.request('/turtle/screener/latest');
  }

  async getTurtleScreenerHistory() {
    return this.request('/turtle/screener/history');
  }

  async runTurtleScreener(filters) {
    // Vercel 환경을 고려한 단순 응답
    return this.request('/turtle/run-screener', { method: 'POST', body: JSON.stringify({ filters }) });
  }

  async saveTurtleMultiScreener(hits) {
    return this.request('/turtle/multi-screener/save', {
      method: 'POST',
      body: JSON.stringify({ hits })
    });
  }

  async loadTurtleMultiScreener() {
    return this.request('/turtle/multi-screener/load');
  }

  // --- Minervini VCP Trading ---
  async getMinerviniConfig() {
    return this.request('/minervini/config');
  }

  async updateMinerviniConfig(config) {
    return this.request('/minervini/config', { method: 'POST', body: JSON.stringify(config) });
  }

  async getMinerviniPositions() {
    return this.request('/minervini/positions');
  }

  async getMinerviniAllPositions() {
    return this.request('/minervini/positions/all');
  }

  async getMinerviniTrades(limit = 200) {
    return this.request(`/minervini/trades?limit=${limit}`);
  }

  async getMinerviniPerformance(limit = 60) {
    return this.request(`/minervini/performance?limit=${limit}`);
  }

  async getMinerviniSignalPreview() {
    return this.request('/minervini/signal-preview');
  }

  async runMinerviniScan(onData) {
    const url = `${this.baseURL}/minervini/run`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            if (onData) onData(data);
          } catch (e) { console.error('SSE Parse Error', e); }
        }
      }
    }
  }

  async resetMinerviniAll() {
    return this.request('/minervini/reset', { method: 'POST' });
  }

  async getMinerviniStats() {
    return this.request('/minervini/stats');
  }

  async getMinerviniScheduler() {
    return this.request('/minervini/scheduler');
  }

  async getMinerviniScreener() {
    return this.request('/minervini/screener');
  }

  async getMinerviniScreenerHistory() {
    return this.request('/minervini/screener/history');
  }

  async runMinerviniScreener(filters, onData) {
    const url = `${this.baseURL}/minervini/screener/run`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters }),
    });
    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            onData(data);
          } catch (e) { console.error('SSE Parse Error', e); }
        }
      }
    }
  }

  // --- Multi Indicator Screener & Simulation ---
  async runTurtleMultiScreenerStream(date, strategies, market, onData) {
    const url = `${this.baseURL}/turtle/multi-screener-stream`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date, strategies, filters: { market } }),
    });

    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();

      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            onData(data);
          } catch (e) {
            console.error('SSE Parse Error', e);
          }
        }
      }
    }
  }

  async runTurtleMultiSimulate(hits, fromDate) {
    return this.request('/turtle/multi-simulate', {
      method: 'POST',
      body: JSON.stringify({ hits, fromDate })
    });
  }

  // --- VWAP Screener ---
  async runVwapScreener(filters, onData) {
    const url = `${this.baseURL}/vwap-screener/run`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters }),
    });
    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            onData(data);
          } catch (e) { console.error('SSE Parse Error', e); }
        }
      }
    }
  }

  async getVwapScreenerHistory() {
    return this.request('/vwap-screener/history');
  }

  async getVwapScreenerResult(id) {
    return this.request(`/vwap-screener/history/${id}`);
  }

  // ─── DTL-2Stage Screener ───────────────────────────────────────────────────
  // SSE 스트리밍 스캔 실행
  async runDtlScreener(filters, onData) {
    const url = `${this.baseURL}/dtl-screener/run`;
    const response = await fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ filters }),
    });
    if (!response.ok) throw new Error(`HTTP error! ${response.status}`);

    const reader  = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try { onData(JSON.parse(part.slice(6))); } catch {}
        }
      }
    }
  }

  // ─── DTL 워치리스트 ────────────────────────────────────────────────────────
  async getDtlWatchlist(all = false) {
    return this.request(`/dtl-screener/watchlist${all ? '?all=true' : ''}`);
  }

  async addDtlWatchlist(data) {
    return this.request('/dtl-screener/watchlist', {
      method: 'POST',
      body:   JSON.stringify(data),
    });
  }

  async deleteDtlWatchlist(symbol) {
    return this.request(`/dtl-screener/watchlist/${symbol}`, { method: 'DELETE' });
  }

  async refreshDtlWatchlist() {
    return this.request('/dtl-screener/watchlist/refresh', { method: 'POST' });
  }

  // ─── DTL Focus List ────────────────────────────────────────────────────────
  async getDtlFocusList() {
    return this.request('/dtl-screener/focus-list');
  }

  async runDtlFocusListStream(onData) {
    const url = `${this.baseURL}/dtl-screener/focus-list/run`;
    const response = await fetch(url, { method: 'POST' });
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split('\n\n');
      buffer = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const data = JSON.parse(part.slice(6));
            if (data.error) throw new Error(data.error);
            onData(data);
          } catch (e) {
            console.error('FocusList SSE parse error', e);
          }
        }
      }
    }
  }

  // ─── 주역×사주 AI ───────────────────────────────────────
  interpretIching({ sajuText, sajuStructured, question, gua, userId }) {
    return this.request('/iching/interpret', {
      method: 'POST',
      body: JSON.stringify({ sajuText, sajuStructured, question, gua, userId }),
    });
  }

  submitIchingFeedback(sessionId, feedbackScore, sajuHelped) {
    const body = { feedbackScore };
    if (sajuHelped !== undefined) body.sajuHelped = sajuHelped;
    return this.request(`/iching/feedback/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  getIchingHistory(userId) {
    return this.request(`/iching/history?userId=${encodeURIComponent(userId)}`);
  }

  // ─── 상한가 레이더 (limit_up_radar) ───────────────────────
  getRadarEvents({ date, ticker, market, materialType, minConfidence, limit } = {}) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (ticker) params.set('ticker', ticker);
    if (market) params.set('market', market);
    if (materialType) params.set('material_type', materialType);
    if (minConfidence != null) params.set('min_confidence', minConfidence);
    if (limit) params.set('limit', limit);
    const qs = params.toString();
    return this.request(`/radar/events${qs ? `?${qs}` : ''}`);
  }

  getRadarEvent(eventId) {
    return this.request(`/radar/events/${encodeURIComponent(eventId)}`);
  }

  getRadarTickerStats(ticker) {
    return this.request(`/radar/ticker-stats/${encodeURIComponent(ticker)}`);
  }

  // 특정 종목의 상한가 발생 이력 전체(날짜 무관, 최신순) — 상세페이지 "과거 상한가 이력" 리스트용
  getRadarTickerEvents(ticker, limit = 100) {
    return this.getRadarEvents({ ticker, limit });
  }

  getRadarSummary() {
    return this.request('/radar/summary');
  }

  // ─── 기관수급분석 (inst_flow_radar) ───────────────────────
  getInstFlowDaily(date) {
    return this.request(`/inst-flow/daily${date ? `?date=${date}` : ''}`);
  }

  getInstFlowHistory(days = 60) {
    return this.request(`/inst-flow/history?days=${days}`);
  }

  getInstFlowSummary() {
    return this.request('/inst-flow/summary');
  }

  getInstFlowIntraday(date) {
    return this.request(`/inst-flow/intraday${date ? `?date=${date}` : ''}`);
  }
}

// 싱글톤 인스턴스 생성
const apiService = new ApiService();

export default apiService;

